import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HttpService, isLiliError, isSpecificLiliError } from '@Services/index';
import { useBoundStore } from '@Store/useBoundStore';
import { useNavigationManager } from '@Router/useNavigationManager';
import { useCountdownTimer } from '@Hooks/useTimer';
import { ErrorModalTypes } from '@Types/index';
import { RoutesMap } from '@Router/router.types';
import { AuthErrorMap } from '@Services/errors/auth.errors';
import { Icons } from '@Components/icon/Icon';
import type { ServerErrorResponse } from '@Types/index';
import type { RequestMfaCodeResponse } from '../reset-password-types';

const useRequestMfaCode = () => {
  const defaultMfaType = 'SMS';
  return useMutation({
    mutationFn: async ({ email, requestMfaType }: { email: string; requestMfaType: string }) => {
      const response = await HttpService.post('v1/onboarding/user/openauth/recovery/start', {
        loginName: email,
        mfaType: requestMfaType || defaultMfaType,
      });
      return response.data as RequestMfaCodeResponse;
    },
  });
};

interface VerifyCodeResponse {
  token: string;
  isUs: boolean;
  stateToken: string;
}

const useVerifyCode = (onError: (error: ServerErrorResponse) => void) => {
  const { requestMfaType, customerId } = useBoundStore((state) => state.resetPasswordSlice);
  return useMutation({
    mutationFn: async ({ otpAnswer }: { otpAnswer: string }) => {
      const response = await HttpService.post<VerifyCodeResponse>(
        `v1/onboarding/user/openauth/${customerId}/recovery/mfa`,
        {
          answer: otpAnswer,
          mfaType: requestMfaType,
        }
      );
      return response?.data;
    },
    onError: (error: ServerErrorResponse) => {
      onError(error);
    },
  });
};
export const useVerificationCodeLogic = () => {
  const { email, requestMfaType, setStateToken, setIsUs, setToken } = useBoundStore(
    (state) => state.resetPasswordSlice
  );
  const { mutateAsync, isPending, isIdle } = useRequestMfaCode();
  const [error, setError] = useState<string | undefined>(undefined);
  const { mutateAsync: verifyCode, isPending: isVerifyCodePending } = useVerifyCode((errData) => {
    setError(errData?.response?.data?.errors[0]?.localizedMessage || '');
  });
  const { setCustomerId, setMfaDestination } = useBoundStore((state) => state.resetPasswordSlice);
  const { navigateToNextPage, navigateToSpecificPage } = useNavigationManager();
  const { padTimeLeft, startTimer } = useCountdownTimer({
    intervalDuration: 30,
    onFinishCountdown: () => {},
  });
  const setErrorModal = useBoundStore((state) => state.errorSlice.setErrorModal);
  const { t } = useTranslation('enErrors');

  const storeResponseData = (responseData: RequestMfaCodeResponse) => {
    setCustomerId(responseData.customerId);
    setMfaDestination(responseData.mfaDestination);
  };

  const handleVerifyCode = async (otpAnswer: string) => {
    const verifyCodeResponse = await verifyCode({ otpAnswer });
    setStateToken(verifyCodeResponse.stateToken);
    setIsUs(verifyCodeResponse.isUs);
    setToken(verifyCodeResponse.token);
    navigateToNextPage(RoutesMap.RECOVERY_VERIFICATION_CODE);
  };

  const requestMfaCode = async () => {
    try {
      const responseData = await mutateAsync({ email, requestMfaType });
      storeResponseData(responseData);
      startTimer();
    } catch (err) {
      const errorMessage = isLiliError(err)
        ? err.response?.data?.errors[0]?.localizedMessage
        : t('default.title');

      const isCustomerNotFound = isSpecificLiliError(
        err,
        AuthErrorMap.CUSTOMER_NOT_FOUND.errorCode
      );
      const errorKey = isCustomerNotFound ? 'authCustomerNotFound' : 'default';

      setErrorModal({
        error: errorMessage,
        modalProps: {
          title: t(`${errorKey}.title`),
          content: t(`${errorKey}.description`),
          icon: Icons.Oops,
          buttonText: t(`${errorKey}.button`),
          type: ErrorModalTypes.GENERAL,
          onSubmit: () => {
            navigateToSpecificPage(RoutesMap.RESET_PASSWORD_REQUEST);
          },
        },
      });
    }
  };

  const clearError = () => {
    setError(undefined);
  };

  useEffect(() => {
    requestMfaCode();
  }, []);

  return {
    isPending: isPending || isVerifyCodePending,
    isIdle,
    requestMfaCode,
    handleVerifyCode,
    padTimeLeft,
    error,
    clearError,
  };
};
